import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, ChevronDown, ChevronUp, CalendarClock, X } from "lucide-react";
import api, { errText } from "@/lib/api";

export default function Broadcast() {
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", template_name: "", language: "en", audience_type: "all", tags: [], phones: "", body_params: [], header_image_url: "", schedule_mode: "now", scheduled_at: "" });
  const [tplSel, setTplSel] = useState(null);

  const load = async () => {
    try {
      const [b, t, tg] = await Promise.all([api.get("/broadcasts"), api.get("/templates"), api.get("/contacts/tags")]);
      setBroadcasts(b.data);
      setTemplates(t.data.filter((x) => x.status === "APPROVED"));
      setTags(tg.data);
    } catch (e) {}
  };

  useEffect(() => {
    load();
    const t = setInterval(() => api.get("/broadcasts").then((r) => setBroadcasts(r.data)).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, []);

  const paramCount = (tpl) => (((tpl?.components || []).find((c) => c.type === "BODY") || {}).text || "").match(/\{\{\d+\}\}/g)?.length || 0;
  const hasImageHeader = (tpl) => (tpl?.components || []).some((c) => c.type === "HEADER" && c.format === "IMAGE");

  const pickTemplate = (name) => {
    const t = templates.find((x) => x.name === name);
    setTplSel(t || null);
    setForm((f) => ({ ...f, template_name: name, language: t?.language || "en", body_params: t ? Array(paramCount(t)).fill("") : [], header_image_url: "" }));
  };

  const launch = async () => {
    setBusy(true);
    try {
      await api.post("/broadcasts", {
        ...form,
        phones: form.phones ? form.phones.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean) : [],
        header_image_url: form.header_image_url || null,
        scheduled_at: form.schedule_mode === "later" && form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
      });
      toast.success(form.schedule_mode === "later" ? "Campaign scheduled!" : "Broadcast launched!");
      setForm({ name: "", template_name: "", language: "en", audience_type: "all", tags: [], phones: "", body_params: [], header_image_url: "", schedule_mode: "now", scheduled_at: "" });
      setTplSel(null);
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelCampaign = async (id) => {
    try {
      await api.delete(`/broadcasts/${id}`);
      toast.success("Campaign cancelled");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const toggleExpand = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    try {
      const { data } = await api.get(`/broadcasts/${id}`);
      setDetail(data);
    } catch (e) {}
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      <h1 className="font-heading text-2xl font-extrabold">Broadcasting</h1>
      <div className="font-hand text-lg text-amber-800 mb-5">send approved templates to your audience ✦</div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-tactile card-yellow p-5 space-y-3 h-fit" data-testid="broadcast-builder">
          <div className="font-heading font-bold text-lg">New Campaign</div>
          <div>
            <label className="label-upper">Campaign Name</label>
            <input data-testid="bc-name-input" className="input-tactile" placeholder="Diwali Sale Blast" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="label-upper">Step 1 · Approved Template</label>
            <select data-testid="bc-template-select" className="input-tactile" value={form.template_name} onChange={(e) => pickTemplate(e.target.value)}>
              <option value="">Select template...</option>
              {templates.map((t) => <option key={t.id || t.name} value={t.name}>{t.name} ({t.language})</option>)}
            </select>
            {tplSel && <div className="text-xs bg-white border rounded-lg p-2 mt-1.5 whitespace-pre-wrap" style={{ borderColor: "var(--cd-navy)" }}>{((tplSel.components || []).find((c) => c.type === "BODY") || {}).text}</div>}
          </div>
          <div>
            <label className="label-upper">Step 2 · Audience</label>
            <div className="flex gap-1.5 mb-2">
              {["all", "tags", "phones"].map((a) => (
                <button key={a} data-testid={`bc-audience-${a}`} className="text-xs font-bold px-3 py-1 rounded-full border-2" style={form.audience_type === a ? { background: "var(--cd-navy)", color: "#fff", borderColor: "var(--cd-navy)" } : { borderColor: "var(--cd-navy)", background: "#fff" }} onClick={() => setForm({ ...form, audience_type: a })}>
                  {a === "all" ? "All Contacts" : a === "tags" ? "By Tags" : "Phone List"}
                </button>
              ))}
            </div>
            {form.audience_type === "tags" && (
              <div className="flex flex-wrap gap-1.5">
                {tags.length === 0 && <span className="text-xs text-slate-500">No tags yet — add tags to contacts first.</span>}
                {tags.map((t) => (
                  <button key={t} className="text-xs font-bold px-2.5 py-1 rounded-full border-2" style={form.tags.includes(t) ? { background: "var(--cd-green)", borderColor: "var(--cd-navy)" } : { borderColor: "var(--cd-navy)", background: "#fff" }} onClick={() => setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }))}>
                    {t}
                  </button>
                ))}
              </div>
            )}
            {form.audience_type === "phones" && (
              <textarea data-testid="bc-phones-input" className="input-tactile" rows={3} placeholder="919958709623, 9198xxxxxx (comma or newline separated)" value={form.phones} onChange={(e) => setForm({ ...form, phones: e.target.value })} />
            )}
          </div>
          {(form.body_params.length > 0 || (tplSel && hasImageHeader(tplSel))) && (
            <div>
              <label className="label-upper">Step 3 · Variable Mapping</label>
              {tplSel && hasImageHeader(tplSel) && (
                <input data-testid="bc-header-url-input" className="input-tactile mb-1.5" placeholder="Header image URL (https://...)" value={form.header_image_url} onChange={(e) => setForm({ ...form, header_image_url: e.target.value })} />
              )}
              {form.body_params.map((p, i) => (
                <input key={i} data-testid={`bc-param-${i}`} className="input-tactile mb-1.5" placeholder={`{{${i + 1}}} — use {{name}} for contact name`} value={p} onChange={(e) => setForm((f) => ({ ...f, body_params: f.body_params.map((x, j) => (j === i ? e.target.value : x)) }))} />
              ))}
              <div className="text-[11px] text-slate-600">Tip: type <span className="font-mono2 font-bold">{"{{name}}"}</span> to personalize with each contact's name.</div>
            </div>
          )}
          <div>
            <label className="label-upper">Step 4 · Launch Mode</label>
            <div className="flex gap-1.5 mb-2">
              <button data-testid="bc-mode-now" className="text-xs font-bold px-3 py-1 rounded-full border-2" style={form.schedule_mode === "now" ? { background: "var(--cd-navy)", color: "#fff", borderColor: "var(--cd-navy)" } : { borderColor: "var(--cd-navy)", background: "#fff" }} onClick={() => setForm({ ...form, schedule_mode: "now" })}>
                Send Now
              </button>
              <button data-testid="bc-mode-later" className="text-xs font-bold px-3 py-1 rounded-full border-2 inline-flex items-center gap-1" style={form.schedule_mode === "later" ? { background: "var(--cd-navy)", color: "#fff", borderColor: "var(--cd-navy)" } : { borderColor: "var(--cd-navy)", background: "#fff" }} onClick={() => setForm({ ...form, schedule_mode: "later" })}>
                <CalendarClock size={12} /> Schedule
              </button>
            </div>
            {form.schedule_mode === "later" && (
              <>
                <input data-testid="bc-schedule-input" type="datetime-local" className="input-tactile" value={form.scheduled_at} min={new Date(Date.now() + 60000).toISOString().slice(0, 16)} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
                <div className="text-[11px] text-slate-600 mt-1">Scheduled campaigns dispatch in ~15-minute windows.</div>
              </>
            )}
          </div>
          <button data-testid="bc-launch-btn" className="btn-navy w-full justify-center py-2.5" disabled={busy || !form.name || !form.template_name || (form.schedule_mode === "later" && !form.scheduled_at)} onClick={launch}>
            {form.schedule_mode === "later" ? <CalendarClock size={15} /> : <Send size={15} />} {busy ? "Working..." : form.schedule_mode === "later" ? "Schedule Broadcast" : "Launch Broadcast"}
          </button>
        </div>

        <div className="space-y-3">
          <div className="font-heading font-bold text-lg">Campaigns</div>
          {broadcasts.length === 0 && <div className="font-hand text-lg text-amber-800">no campaigns yet...</div>}
          {broadcasts.map((b) => (
            <div key={b.id} className="card-tactile p-4" data-testid={`broadcast-card-${b.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold">{b.name}</div>
                  <div className="text-xs text-slate-500 font-mono2">{b.template_name} · {new Date(b.created_at).toLocaleString()}</div>
                </div>
                <span className={`status-badge ${b.status === "completed" ? "badge-approved" : b.status === "cancelled" ? "badge-rejected" : "badge-pending"}`}>{b.status}</span>
              </div>
              {b.status === "scheduled" && (
                <div className="flex items-center justify-between mt-1.5 text-xs font-semibold bg-[var(--cd-blue)] border rounded-lg px-2 py-1" style={{ borderColor: "var(--cd-navy)" }}>
                  <span className="inline-flex items-center gap-1"><CalendarClock size={12} /> {new Date(b.scheduled_at).toLocaleString()}</span>
                  <button data-testid={`bc-cancel-${b.id}`} className="btn-ghost2 py-0.5 text-red-600" onClick={() => cancelCampaign(b.id)}><X size={12} /> Cancel</button>
                </div>
              )}
              <div className="mt-2.5 h-2.5 rounded-full bg-slate-200 overflow-hidden border" style={{ borderColor: "var(--cd-navy)" }}>
                <div className="h-full transition-all" style={{ width: `${b.total ? ((b.sent + b.failed) / b.total) * 100 : 0}%`, background: "#25D366" }} />
              </div>
              <div className="flex items-center justify-between mt-1.5 text-xs font-semibold">
                <span>{b.sent} sent · <span className="text-red-600">{b.failed} failed</span> · {b.total} total</span>
                <button className="btn-ghost2 py-0.5" data-testid={`bc-expand-${b.id}`} onClick={() => toggleExpand(b.id)}>
                  {expanded === b.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Report
                </button>
              </div>
              {expanded === b.id && detail?.broadcast?.id === b.id && (
                <div className="mt-2 pt-2 border-t" style={{ borderColor: "#E5DCB8" }}>
                  <div className="flex gap-3 text-xs font-bold mb-2">
                    <span className="text-green-700">✓ delivered {detail.counts.delivered}</span>
                    <span className="text-blue-700">👁 read {detail.counts.read}</span>
                    <span className="text-red-700">✗ failed {detail.counts.failed}</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {detail.recipients.map((r) => (
                      <div key={r.id} className="text-xs bg-black/[0.03] rounded px-2 py-1" data-testid={`bc-recipient-${r.phone}`}>
                        <div className="flex justify-between">
                          <span className="font-mono2">{r.name || r.phone}</span>
                          <span className={r.status === "failed" ? "text-red-600 font-bold" : "text-slate-600"}>{r.status}</span>
                        </div>
                        {r.error && <div className="text-red-700 mt-0.5 break-words" data-testid={`bc-recipient-error-${r.phone}`}>⚠ {r.error}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
